import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Image, Progress } from "antd";
import { ProjectContext } from "context/ProjectProvider";
import { useParams } from "react-router";
import { getAIMLProjectSummaryData } from "services/auth";
import StepsLayout from "../steps/layout-steps";
import AiPoweredUI from "../ai-powered";
import { TSummary } from "../model";

const loadingImages = [
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence1.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence2.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence3.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence4.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence5.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence6.gif",
  "https://public-constructiviq.s3.us-east-1.amazonaws.com/assets/AI_Animation+Slides+Master_Sequence7.gif"
];

export default function WelcomeLoadingPage({
  onProcessCompleted
}: {
  onProcessCompleted: (summary: TSummary) => void;
}) {
  const { tokenRetrievalState } = useContext(ProjectContext);
  const { projectId } = useParams() as any;
  const [progressPercent, setProgressPercent] = useState(0);
  const [summary, setSummary] = useState<TSummary>();
  const [imageSrc, setImageSrc] = useState(loadingImages[0]);

  const [text, setText] = useState(
    "Getting your Documents and Drawings together..."
  );

  // 🔹 Preload images
  useEffect(() => {
    loadingImages.forEach((url) => {
      const img = new window.Image();
      img.src = url;
    });
  }, []);

  const aiPoweredStatusArr = useMemo(() => {
    return [
      "Extracting Material Schedules from your Drawings...",
      "Creating Smart Links..."
    ];
  }, []);

  const getSummaryData = useCallback(async () => {
    const response = await getAIMLProjectSummaryData(
      tokenRetrievalState.token,
      projectId
    );
    if (response.success) {
      const { data } = response;
      setSummary(data?.body?.summary);
    } else {
      console.error(response.error);
    }
  }, [projectId, tokenRetrievalState.token]);

  useEffect(() => {
    getSummaryData();
  }, [getSummaryData]);

  const timerRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      // Creates an interval which will update the current data every minute
      // This will trigger a rerender every component that uses the useDate hook.
      setProgressPercent((pre) => {
        switch (pre) {
          case 10:
            setText("Synchronizing Submittals...");
            setImageSrc(loadingImages[1]);
            break;
          case 25:
            setText("Understanding your Schedule...");
            setImageSrc(loadingImages[2]);

            break;
          case 35:
            setText("Extracting Material Schedules from your Drawings...");
            setImageSrc(loadingImages[3]);
            break;
          case 55:
            setText("Creating Procurement Log...");
            setImageSrc(loadingImages[4]);

            break;
          case 70:
            setText("Creating Smart Links...");
            setImageSrc(loadingImages[5]);
            break;
          case 90:
            setText("Creating your Procurement Plan...");
            setImageSrc(loadingImages[6]);

            break;
          case 91:
            if (!summary) return 91;
            break;
          case 100:
            setText("");
            clearInterval(timerRef.current);
            // case 91 above blocks progress past 91 until summary is truthy, so reaching case 100 guarantees summary is set; the falsy branch is unreachable
            /* v8 ignore next */
            if (summary) onProcessCompleted(summary);
            return 100;
          default:
            break;
        }

        return pre + 1;
      });
    }, 1000);

    return () => {
      // timerRef.current is always assigned by setInterval above before this cleanup is registered, so the falsy branch is a teardown race guard that cannot be hit
      /* v8 ignore next */
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [getSummaryData, onProcessCompleted, summary]);

  const isAIPoweredStatus = useMemo(() => {
    return aiPoweredStatusArr.includes(text);
  }, [aiPoweredStatusArr, text]);

  return (
    <div className="p-6">
      <StepsLayout title="Smart Setup">
        <div className="w-full h-full flex justify-center items-center">
          <div className="flex-col space-y-4 flex justify-center items-center">
            <div className="flex items-center justify-center p-2 rounded-xl h-[360px]">
              <Image src={imageSrc} height={360} preview={false} />
            </div>

            <div className="font-semibold">{text}</div>
            <Progress className="w-96" percent={progressPercent} />
            <div className="h-10">{isAIPoweredStatus && <AiPoweredUI />}</div>
          </div>
        </div>
      </StepsLayout>
    </div>
  );
}
